<template>
  <div class="biotime-attendance-page">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-primary">
        <div class="card-icon">
          <md-icon>fingerprint</md-icon>
        </div>
        <h4 class="title">Biometric Attendance Records</h4>
        <p class="category">View all biometric terminal punch records</p>
      </md-card-header>

      <md-card-content>
        <!-- Today's Stats Cards -->
        <div class="stats-cards md-layout md-gutter" v-if="todayStats">
          <div class="md-layout-item md-size-20" @click="setFilter('state', '0')">
            <div class="stat-card primary">
              <div class="stat-value">{{ formatNumber(todayStats.check_in || 0) }}</div>
              <div class="stat-label">Today's Check-Ins</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20" @click="setFilter('state', '1')">
            <div class="stat-card success">
              <div class="stat-value">{{ formatNumber(todayStats.check_out || 0) }}</div>
              <div class="stat-label">Today's Check-Outs</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20">
            <div class="stat-card info">
              <div class="stat-value">{{ formatNumber(todayStats.unique_employees || 0) }}</div>
              <div class="stat-label">Active Employees</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20" @click="setFilter('state', '2')">
            <div class="stat-card warning">
              <div class="stat-value">{{ formatNumber(todayStats.attendances || 0) }}</div>
              <div class="stat-label">Total Attendance</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20" @click="setFilter('processed', '0')">
            <div class="stat-card pending">
              <div class="stat-value">{{ formatNumber(todayStats.unmatched || 0) }}</div>
              <div class="stat-label">Unprocessed</div>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters-bar md-layout md-gutter">
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>From Date</label>
              <md-input type="date" v-model="filters.from_date" @change="onFilterChange" />
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>To Date</label>
              <md-input type="date" v-model="filters.to_date" @change="onFilterChange" />
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>State</label>
              <md-select v-model="filters.state" @md-selected="onFilterChange">
                <md-option value="">All States</md-option>
                <md-option value="0">Check In</md-option>
                <md-option value="1">Check Out</md-option>
                <md-option value="2">Attendance</md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>Status</label>
              <md-select v-model="filters.processed" @md-selected="onFilterChange">
                <md-option value="">All</md-option>
                <md-option value="0">Unprocessed</md-option>
                <md-option value="1">Processed</md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Per Page</label>
              <md-select v-model="pagination.perPage" @md-selected="onPerPageChange">
                <md-option v-for="item in perPageOptions" :key="item" :value="item">
                  {{ item }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>Search</label>
              <md-input v-model="filters.search" @input="onSearchChange" placeholder="Search..." />
            </md-field>
          </div>
        </div>

        <!-- Bulk Actions -->
        <div class="bulk-actions" v-if="selectedAttendances && selectedAttendances.length > 0">
          <span class="selected-count">{{ selectedAttendances.length }} record(s) selected</span>
          <md-button class="md-primary md-raised" @click="processSelected">
            <md-icon>play_arrow</md-icon> Process Selected
          </md-button>
          <md-button class="md-danger md-raised" @click="clearSelection">
            Clear Selection
          </md-button>
        </div>

        <!-- Loading -->
        <div v-if="isPageLoading" class="loading-container">
          <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
        </div>

        <!-- Attendance Table -->
        <div class="attendance-table" v-else>
          <md-table v-if="attendancesData && attendancesData.length">
            <md-table-row v-for="item in attendancesData" :key="item.id" :class="{ 'processed-row': item.prossed }">

              <md-table-cell md-label="">
                <md-checkbox v-model="selectedAttendances" :value="item" />
              </md-table-cell>
              <md-table-cell md-label="Employee Code">
                <strong>{{ item.emp_code }}</strong>
              </md-table-cell>
              <md-table-cell md-label="Employee Name">
                <span v-if="item.employee">
                  {{ getEmployeeName(item.employee) }}
                </span>
                <span v-else class="text-muted">Unknown</span>
              </md-table-cell>
              <md-table-cell md-label="Date/Time">
                {{ formatDateTime(item.local_punch_time) }}
              </md-table-cell>
              <md-table-cell md-label="State">
                <badge :type="getStateBadge(item.state)">
                  {{ formatState(item.state) }}
                </badge>
              </md-table-cell>
              <md-table-cell md-label="Device">
                {{ item.terminal_alias || 'N/A' }}
              </md-table-cell>
              <md-table-cell md-label="Status">
                <badge :type="item.processed ? 'success' : 'warning'">
                  {{ item.processed ? 'Processed' : 'Pending' }}
                </badge>
              </md-table-cell>
              <md-table-cell md-label="Actions">
                <md-button class="md-icon-button md-primary" @click="viewDetails(item)" title="View Details">
                  <md-icon>visibility</md-icon>
                </md-button>
                <md-button
                    v-if="!item.prossed"
                    class="md-icon-button md-success"
                    @click="processSingle(item)"
                    title="Process to Schedule"
                >
                  <md-icon>play_arrow</md-icon>
                </md-button>
                <md-button class="md-icon-button md-info" @click="viewEmployee(item.employee)" title="View Employee" v-if="item.employee">
                  <md-icon>person</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <div v-else class="empty-state">
            <md-icon>fingerprint</md-icon>
            <p>No attendance records found</p>
          </div>
        </div>


        <div class="pagination-footer">
          <div>
            <p class="card-category">
              Showing {{ from + 1 }} to {{ to }} of {{ totalItems }} entries
            </p>
          </div>
          <pagination
              class="pagination-no-border pagination-success"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="totalItems"
          />
        </div>
      </md-card-content>
    </md-card>

    <!-- Process Confirmation Modal -->
    <md-dialog :md-active.sync="modal.process.active" class="process-modal">
      <md-dialog-title>Process Attendance Records</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <p>You are about to process <strong>{{ modal.process.count || 0 }}</strong> attendance record(s).</p>
          <p>This will:</p>
          <ul>
            <li>Match the attendance with labourer schedules based on time</li>
            <li>Update the labourer schedule with check-in/out information</li>
            <li>Mark these records as processed</li>
          </ul>

          <md-field>
            <label>Select Action</label>
            <md-select v-model="modal.process.action" required>
              <md-option value="auto">Auto-match with schedules</md-option>
              <md-option value="manual">Manual assignment (requires supervisor)</md-option>
            </md-select>
          </md-field>

          <md-field>
            <label>Remarks (Optional)</label>
            <md-textarea v-model="modal.process.remarks" rows="2" />
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="confirmProcess" :disabled="isProcessing">
          {{ isProcessing ? 'Processing...' : 'Confirm Process' }}
        </md-button>
        <md-button class="md-danger" @click="closeProcessModal">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- View Details Modal -->
    <md-dialog :md-active.sync="modal.details.active" class="details-modal">
      <md-dialog-title>Attendance Details</md-dialog-title>
      <md-dialog-content>
        <div class="details-content" v-if="selectedAttendance">
          <div class="details-section">
            <h5>Employee Information</h5>
            <div class="details-grid">
              <div><strong>Employee Code:</strong> {{ selectedAttendance.emp_code }}</div>
              <div><strong>Employee Name:</strong> {{ getEmployeeName(selectedAttendance.employee) }}</div>
              <div><strong>NIDA Number:</strong> {{ getNidaNumber(selectedAttendance.employee) }}</div>
            </div>
          </div>

          <div class="details-section">
            <h5>Attendance Information</h5>
            <div class="details-grid">
              <div><strong>Date/Time:</strong> {{ formatDateTime(selectedAttendance.time) }}</div>
              <div><strong>State:</strong> <badge :type="getStateBadge(selectedAttendance.state)">{{ formatState(selectedAttendance.state) }}</badge></div>
              <div><strong>Verify Type:</strong> {{ getVerifyType(selectedAttendance) }}</div>
              <div><strong>Work Code:</strong> {{ selectedAttendance.work_code || 'N/A' }}</div>
            </div>
          </div>

          <div class="details-section">
            <h5>Terminal Information</h5>
            <div class="details-grid">
              <div><strong>Device:</strong> {{ selectedAttendance.device || 'N/A' }}</div>
              <div><strong>Terminal SN:</strong> {{ selectedAttendance.terminal_sn || 'N/A' }}</div>
              <div><strong>Terminal Alias:</strong> {{ selectedAttendance.terminal_alias || 'N/A' }}</div>
              <div><strong>Area Alias:</strong> {{ selectedAttendance.area_alias || 'N/A' }}</div>
            </div>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closeDetailsModal">Close</md-button>
        <md-button v-if="selectedAttendance && !selectedAttendance.prossed" class="md-success" @click="processFromDetails">
          Process This Record
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DateFormat from '@/mixins/date-format-mixin';
import Badge from '@/components/Badge.vue';
import Pagination from '@/components/Pagination';
import formMixin from '@/mixins/form-mixin';
import _ from 'lodash';

export default {
  name: 'ListBiotimeAttendancePage',
  components: {
    Badge,
    Pagination
  },
  mixins: [formMixin,DateFormat],
  data: function() {
    return {
      attendancesData: [],
      sortation:{field:'time',direction:'desc'},
      selectedAttendances: [],
      filters: {
        from_date: null,
        to_date: null,
        state: '',
        processed: '',
        search: ''
      },
      pagination: {
        currentPage: 1,
        perPage: 15
      },
      perPageOptions: [10, 15, 25, 50, 100],
      isPageLoading: false,
      isProcessing: false,
      totalItems: 0,
      todayStats: {
        check_in: 0,
        check_out: 0,
        unique_employees: 0,
        late_checkins: 0,
        unmatched: 0
      },
      modal: {
        process: {
          active: false,
          count: 0,
          action: 'auto',
          remarks: ''
        },
        details: {
          active: false
        }
      },
      selectedAttendance: null
    };
  },
  computed: {
    ...mapGetters('biotimeAttendances', [ 'meta']),

    attendances: function() {
      return this.attendancesData || [];
    },

    sort: function() {
      if (this.sortation.order === 'desc') {
        return '-' + this.sortation.field;
      }
      return this.sortation.field;
    },
    from: function() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to: function() {
      var highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    canSelectVendor: function() {
      return this.selectedStation && this.selectedStation !== 'all';
    }
  },
  created: function() {
    var today = new Date();
    var startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.filters.from_date = this.formatDateYMD(startOfMonth);
    this.filters.to_date = this.formatDateYMD(today);

    this.fetchData();
    this.fetchTodayStats();
  },
  methods: {
    ...mapActions('biotimeAttendances', ['list', 'view', 'getTodayStats', 'getStatistics', 'processAttendances']),

    async fetchData() {
      try {
        this.isPageLoading = true;
        var params = {
          include: 'employee',
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage
          },
          filter: {}
        };

        if (this.filters.from_date) {
          params.filter.from_date = this.filters.from_date;
        }

        if (this.filters.to_date) {
          params.filter.to_date = this.filters.to_date;
        }

        if (this.filters.state) {
          params.filter.state = this.filters.state;
        }

        if (this.filters.processed !== '') {
          params.filter.processed = this.filters.processed;
        }

        if (this.filters.search) {
          params.filter.search = this.filters.search;
        }

        await this.list(params);
        const response = this.$store.getters['biotimeAttendances/list'] || [];
        this.attendancesData = response;
        this.totalItems = this.$store.getters['biotimeAttendances/listTotal'] || 0;

      } catch (error) {
        console.error('Error fetching attendances:', error);
        this.notifyError('Failed to load attendance records');
        this.attendancesData = [];
        this.totalItems = 0;
      } finally {
        this.isPageLoading = false;
      }
    },

    async fetchTodayStats() {
      try {
        var stats = await this.getTodayStats();
        this.todayStats = stats || {
          check_in: 0,
          check_out: 0,
          unique_employees: 0,
          late_checkins: 0,
          unmatched: 0
        };
      } catch (error) {
        console.error('Error fetching today stats:', error);
      }
    },

    setFilter: function(field, value) {
      this.filters[field] = this.filters[field] === value ? '' : value;
      this.resetPagination();
      this.fetchData();
    },

    resetPagination: function() {
      this.pagination.currentPage = 1;
    },

    onFilterChange: function() {
      this.resetPagination();
      this.fetchData();
    },

    onPerPageChange: function() {
      this.resetPagination();
      this.fetchData();
    },

    onSearchChange: _.debounce(function() {
      this.resetPagination();
      this.fetchData();
    }, 500),

    onPageChange: function(page) {
      this.pagination.currentPage = page;
      this.fetchData();
    },

    async viewDetails(item) {
      try {
        // Close any open process modal first
        if (this.modal.process.active) {
          this.modal.process.active = false;
        }

        // Fetch full details
        var attendance = await this.view({ id: item.id, include: 'employee' });
        this.selectedAttendance = attendance;
        this.modal.details.active = true;
      } catch (error) {
        console.error('Error fetching attendance details:', error);
        this.notifyError('Failed to load attendance details');
      }
    },

    closeDetailsModal: function() {
      this.modal.details.active = false;
      this.selectedAttendance = null;
    },

    processSingle: function(attendance) {
      // Close details modal if open
      if (this.modal.details.active) {
        this.modal.details.active = false;
      }

      this.selectedAttendances = [attendance];
      this.showProcessModal();
    },

    processSelected: function() {
      if (!this.selectedAttendances || this.selectedAttendances.length === 0) {
        this.notifyWarning('Please select at least one record to process');
        return;
      }

      // Close details modal if open
      if (this.modal.details.active) {
        this.modal.details.active = false;
      }

      this.showProcessModal();
    },

    processFromDetails: function() {
      // Process the current selected attendance from details modal
      if (this.selectedAttendance) {
        this.modal.details.active = false;
        this.processSingle(this.selectedAttendance);
      }
    },

    showProcessModal: function() {
      this.modal.process.count = this.selectedAttendances ? this.selectedAttendances.length : 0;
      this.modal.process.action = 'auto';
      this.modal.process.remarks = '';
      this.modal.process.active = true;
    },

    closeProcessModal: function() {
      this.modal.process.active = false;
      this.selectedAttendances = [];
    },

    async confirmProcess() {
      var self = this;
      this.isProcessing = true;

      try {
        var ids = this.selectedAttendances.map(function(item) {
          return item.id;
        });

        var payload = {
          attendance_ids: ids,
          action: this.modal.process.action,
          remarks: this.modal.process.remarks
        };

        var result = await this.processAttendances(payload);

        this.notifySuccess('Attendance records processed successfully');
        this.closeProcessModal();

        // Refresh data
        await this.fetchData();
        await this.fetchTodayStats();

        if (result && result.matched) {
          this.notifyInfo(result.matched + ' records were matched to schedules');
        }
        if (result && result.unmatched) {
          this.notifyWarning(result.unmatched + ' records could not be matched');
        }
      } catch (error) {
        console.error('Error processing attendances:', error);
        this.notifyError('Failed to process attendance records');
      } finally {
        this.isProcessing = false;
      }
    },

    clearSelection: function() {
      this.selectedAttendances = [];
    },

    viewEmployee: function(employee) {
      if (employee && employee.id) {
        // Close any open modals
        if (this.modal.details.active) {
          this.modal.details.active = false;
        }
        if (this.modal.process.active) {
          this.modal.process.active = false;
        }
        this.$router.push({ name: 'View Labourer', params: { id: employee.id } });
      }
    },

    getEmployeeName: function(employee) {
      if (!employee) return 'Unknown';
      var firstName = employee.first_name || '';
      var lastName = employee.last_name || '';
      var middleName = employee.middle_name || '';
      return [firstName, middleName, lastName].filter(Boolean).join(' ') || employee.emp_code || 'Unknown';
    },

    getNidaNumber: function(employee) {
      return employee ? (employee.nida_number || 'N/A') : 'N/A';
    },

    getVerifyType: function(attendance) {
      return attendance.verify_type_display || attendance.verify_type || 'N/A';
    },

    formatState: function(state) {
      var states = {
        '0': 'Check In',
        '1': 'Check Out',
        '2': 'Attendance'
      };
      return states[state] || state || 'Unknown';
    },

    getStateBadge: function(state) {
      var badges = {
        '0': 'primary',
        '1': 'success',
        '2': 'warning'
      };
      return badges[state] || 'default';
    },

    formatDateTime: function(date) {
      // return date;
      return this.toLocal(date,'DD/MM/YYYY HH:mm:ss')
      // return date
    },

    formatDate: function(date) {
      if (!date) return 'N/A';
      var d = new Date(date);
      return d.toLocaleDateString();
    },

    formatDateYMD: function(date) {
      if (!date) return null;
      var d = new Date(date);
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    },

    formatNumber: function(number) {
      if (!number) return '0';
      return new Intl.NumberFormat().format(number);
    }
  },

  watch: {
    'filters.from_date': function() {
      this.resetPagination();
      this.fetchData();
    },
    'filters.to_date': function() {
      this.resetPagination();
      this.fetchData();
    },
    'filters.state': function() {
      this.resetPagination();
      this.fetchData();
    },
    'filters.processed': function() {
      this.resetPagination();
      this.fetchData();
    },

    pagination: {
      handler: 'fetchData',
      deep: true,
    },
  }
};
</script>

<style scoped>
/* Styles remain the same */
.biotime-attendance-page {
  padding: 20px;
}

.stats-cards {
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card.primary { border-left: 4px solid #2196f3; }
.stat-card.success { border-left: 4px solid #4caf50; }
.stat-card.info { border-left: 4px solid #00bcd4; }
.stat-card.warning { border-left: 4px solid #ff9800; }
.stat-card.pending { border-left: 4px solid #9c27b0; }

.stat-value {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.filters-bar {
  margin-bottom: 24px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;
}

.bulk-actions {
  background: #e3f2fd;
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-count {
  font-weight: 500;
  color: #1976d2;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.processed-row {
  background-color: #e8f5e9;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state md-icon {
  font-size: 64px;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
}

.pagination-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.details-modal .md-dialog,
.process-modal .md-dialog {
  max-width: 600px;
  width: 90%;
}

.details-content {
  max-height: 500px;
  overflow-y: auto;
}

.details-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.details-section h5 {
  margin: 0 0 10px 0;
  color: #9c27b0;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.text-muted {
  color: #999;
}

@media (max-width: 768px) {
  .biotime-attendance-page {
    padding: 10px;
  }

  .filters-bar {
    flex-direction: column;
    gap: 10px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>